/**
 * Client-side image downscaling for uploads. Screenshots sent to the vision
 * model are often multi-MB; shrinking the longest edge (and re-encoding as JPEG)
 * before upload cuts the request size and the vision-call latency that caused
 * timeouts. Pure browser APIs (canvas); falls back to the original bytes if the
 * image can't be decoded so an upload never silently fails.
 */

export interface EncodedImage {
  /** `data:` URL for preview. */
  dataUrl: string;
  /** base64 without the `data:` prefix (wire shape). */
  data: string;
  mimeType: string;
}

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.85;

/** Read a File/Blob to a `data:` URL (no transform). */
function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => resolve(String(r.result));
    r.readAsDataURL(file);
  });
}

function splitDataUrl(dataUrl: string, mimeType: string): EncodedImage | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  return { dataUrl, data: dataUrl.slice(comma + 1), mimeType };
}

/**
 * Downscale an image file so its longest edge is at most `maxEdge`, re-encoding
 * as JPEG (white-matted, so screenshots with alpha don't turn black). If the
 * image is already small enough it is still re-encoded to JPEG to bound size.
 * On any decode/encode failure, returns the original bytes unchanged.
 */
export async function downscaleImage(
  file: File,
  opts: { maxEdge?: number; quality?: number } = {}
): Promise<EncodedImage | null> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  if (!file.type.startsWith("image/")) return null;

  let dataUrl: string;
  try {
    dataUrl = await readAsDataUrl(file);
  } catch {
    return null;
  }
  const original = splitDataUrl(dataUrl, file.type);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode failed"));
      el.src = dataUrl;
    });
    const { width, height } = img;
    if (!width || !height) return original;
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return original;
    ctx.fillStyle = "#ffffff"; // matte so alpha doesn't become black in JPEG
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL("image/jpeg", quality);
    return splitDataUrl(out, "image/jpeg") ?? original;
  } catch {
    return original;
  }
}
